import { useRef } from 'react';
import type { ClipboardEvent, KeyboardEvent, ReactNode } from 'react';

/**
 * Six cases pour un code à six chiffres (planches 27 à 31).
 *
 * Une seule valeur, six champs : on tape, le curseur avance ; on colle « 481 207 »,
 * tout se remplit ; Retour arrière recule. Le premier champ porte
 * `autocomplete="one-time-code"` : sur téléphone, le code reçu par SMS ou
 * courriel est proposé au-dessus du clavier.
 */
export function CodeInput({
    value,
    onChange,
    label,
    invalid = false,
    disabled = false,
    autoFocus = false,
    onComplete,
}: {
    readonly value: string;
    onChange(value: string): void;
    readonly label: string;
    readonly invalid?: boolean;
    readonly disabled?: boolean;
    readonly autoFocus?: boolean;
    onComplete?(value: string): void;
}): ReactNode {
    const refs = useRef<(HTMLInputElement | null)[]>([]);
    const digits = Array.from({ length: 6 }, (_, index) => value[index] ?? '');

    function put(next: string): void {
        const clean = next.replace(/\D/g, '').slice(0, 6);
        onChange(clean);
        refs.current[Math.min(clean.length, 5)]?.focus();

        if (clean.length === 6) {
            onComplete?.(clean);
        }
    }

    function typed(index: number, raw: string): void {
        const all = raw.replace(/\D/g, '');
        // Une case déjà remplie reçoit l'ancien chiffre et le nouveau : on garde le nouveau.
        const digit = digits[index] !== '' && all.length === 2 ? all.slice(-1) : all;

        if (digit.length > 1) {
            put(value.slice(0, index) + digit);

            return;
        }

        if (digit === '') {
            return;
        }

        put(value.slice(0, index) + digit + value.slice(index + 1));
    }

    function key(index: number, event: KeyboardEvent<HTMLInputElement>): void {
        if (event.key === 'Backspace') {
            event.preventDefault();
            const at = digits[index] === '' ? index - 1 : index;

            if (at >= 0) {
                onChange(value.slice(0, at));
                refs.current[at]?.focus();
            }
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            const rtl = document.documentElement.dir === 'rtl';
            const step = (event.key === 'ArrowLeft') !== rtl ? -1 : 1;
            refs.current[Math.max(0, Math.min(5, index + step))]?.focus();
        }
    }

    function paste(event: ClipboardEvent<HTMLInputElement>): void {
        event.preventDefault();
        put(event.clipboardData.getData('text'));
    }

    return (
        <div className={`code-input${invalid ? ' code-input--invalid' : ''}`} role="group" aria-label={label} dir="ltr">
            {digits.map((digit, index) => (
                <span key={index} className="code-input__slot">
                    {index === 3 ? <span className="code-input__dash" aria-hidden="true" /> : null}
                    <input
                        ref={(element) => {
                            refs.current[index] = element;
                        }}
                        className="code-input__box"
                        type="text"
                        inputMode="numeric"
                        autoComplete={index === 0 ? 'one-time-code' : 'off'}
                        maxLength={6}
                        aria-label={`${label} ${index + 1}/6`}
                        aria-invalid={invalid ? true : undefined}
                        disabled={disabled}
                        autoFocus={autoFocus && index === 0}
                        value={digit}
                        onChange={(event) => typed(index, event.target.value)}
                        onKeyDown={(event) => key(index, event)}
                        onPaste={paste}
                        onFocus={(event) => event.target.select()}
                    />
                </span>
            ))}
        </div>
    );
}
